import React, { FC } from 'react';

import { formatter } from 'helpers';
import { useLang } from 'hooks';
import { DashboardLayout } from 'layouts';
import { CardView, InvoiceQr, InvoiceStatus, PaymentStatus } from 'components/layout';
import { Alert, Descriptions, Divider, Grid } from 'components/ui';
import { Invoice, InvoiceStatus as EInvoiceStatus } from 'types/models';

const payableStatuses: EInvoiceStatus[] = [
  EInvoiceStatus.PENDING,
];

type InvoiceViewProps = {
  invoice: Invoice;
};

const InvoiceView: FC<InvoiceViewProps> = ({ invoice }) => {
  const lang = useLang();

  return (
    <DashboardLayout title={lang.get('invoice.single.title', { id: invoice.displayId })}>
      <Grid.Row>

        <Grid.Col md={12}>
          <Grid.Row>
            <Grid.Col>
              <CardView title={lang.get('invoice.single.heading')}>

                <Descriptions
                  items={[{
                    label: lang.get('common.fields.invoice'),
                    children: formatter.formatId(invoice.displayId),
                  }, {
                    label: lang.get('common.fields.product'),
                    children: invoice.purpose,
                  }, {
                    label: lang.get('common.fields.price'),
                    children: formatter.formatCurrency(invoice.amount, invoice.currency),
                  }]}
                />

                <Divider />

                <Descriptions
                  items={[{
                    label: lang.get('common.fields.creationDate'),
                    children: formatter.formatDateTime(invoice.createdDate),
                  }, {
                    label: lang.get('common.fields.updateDate'),
                    children: formatter.formatDateTime(invoice.updatedDate),
                  }, {
                    label: lang.get('common.fields.status'),
                    children: <InvoiceStatus status={invoice.status} />,
                  }]}
                />

              </CardView>
            </Grid.Col>
            <Grid.Col hidden={!payableStatuses.includes(invoice.status)}>
              <CardView title={lang.get('invoice.single.invoice')}>
                <InvoiceQr invoice={invoice} />
              </CardView>
            </Grid.Col>
          </Grid.Row>
        </Grid.Col>

        <Grid.Col md={12}>
          <Grid.Row>
            {invoice.payments.map((payment, index) => (
              <Grid.Col key={payment.trxRef}>
                <CardView title={lang.get('invoice.single.payment', { id: invoice.payments.length - index })}>

                  <Descriptions
                    items={[{
                      children: <Alert type="error" message={payment.errorMessage} />,
                      hidden: !payment.errorMessage,
                    }, {
                      label: lang.get('common.fields.transactionId'),
                      children: payment.trxRef,
                    }, {
                      label: lang.get('common.fields.paymentMethod'),
                      children: lang.get(`common.paymentMethods.${payment.type.toLowerCase()}`),
                    }, {
                      label: lang.get('common.fields.amount'),
                      children: formatter.formatCurrency(payment.amount, payment.currency),
                    }, {
                      label: lang.get('common.fields.fee'),
                      children: formatter.formatCurrency(payment.fee, payment.currency),
                    }, {
                      label: lang.get('common.fields.email'),
                      children: payment.email,
                    }, {
                      label: lang.get('common.fields.notes'),
                      children: payment.purpose,
                    }]}
                  />

                  <Divider />

                  <Descriptions
                    items={[{
                      label: lang.get('common.fields.creationDate'),
                      children: formatter.formatDateTime(payment.createdDate),
                    }, {
                      label: lang.get('common.fields.updateDate'),
                      children: formatter.formatDateTime(payment.updatedDate),
                    }, {
                      label: lang.get('common.fields.status'),
                      children: <PaymentStatus status={payment.status} />,
                    }]}
                  />

                </CardView>
              </Grid.Col>
            ))}
          </Grid.Row>
        </Grid.Col>

      </Grid.Row>
    </DashboardLayout>
  );
};

export default InvoiceView;
